import { useMemo } from "react";
import { Tooltip } from "antd";
import { ApiOutlined, ImportOutlined, ExportOutlined, DollarOutlined } from "@ant-design/icons";
import { BarChart, Bar, XAxis, YAxis, Tooltip as RTooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { fetchAiUsage } from "../../../services/customer360Service";
import {
  cardStyle, num, dateTime, duration, chip, TONE, titleCase,
  StatTile, TileGrid, PanelHeader, PanelTable, PanelSkeleton,
  PanelError, usePanelData,
} from "./shared";

/**
 * The raw call log behind the credits.
 *
 * Where the credit ledger says what was charged, this says what happened: which
 * model, how long it took, and — the reason anyone opens this tab — which calls
 * failed and why.
 */

const usd = (n) => {
  const value = Number(n);
  if (!Number.isFinite(value)) return "—";
  /* Per-call costs are fractions of a cent, so two decimals would round most
     rows to $0.00 and make the column useless. */
  return `$${value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`;
};

const AiUsageTab = ({ userId, active }) => {
  const p = usePanelData(fetchAiUsage, userId, active);

  /* Calls grouped by what was called. Built from the page on screen, so it is a
     shape-of-usage read rather than an account total — the KPI tiles above carry
     the real figures. */
  const byType = useMemo(() => {
    const counts = {};
    (p.rows || []).forEach((r) => {
      const key = titleCase(r.api_call_type || "Other");
      counts[key] = (counts[key] || 0) + 1;
    });
    return Object.entries(counts)
      .map(([name, calls]) => ({ name, calls }))
      .sort((a, b) => b.calls - a.calls)
      .slice(0, 8);
  }, [p.rows]);

  if (p.loading && !p.data) return <PanelSkeleton tiles={4} />;
  if (p.error) return <PanelError error={p.error} onRetry={p.reload} />;

  const t = p.data?.totals || {};

  const columns = [
    {
      title: "When", dataIndex: "created_at", width: 165,
      render: (v) => <span style={{ fontSize: 12.5, color: "#475569", whiteSpace: "nowrap" }}>{dateTime(v)}</span>,
    },
    {
      title: "Call", dataIndex: "api_call_type", width: 170,
      render: (v) => (v ? chip(titleCase(v), TONE.violet) : <span style={{ color: "#cbd5e1" }}>—</span>),
    },
    {
      title: "Model", dataIndex: "model", width: 180,
      render: (v) => <span style={{ fontSize: 12, color: "#334155", fontFamily: "ui-monospace,SFMono-Regular,monospace" }}>{v || "—"}</span>,
    },
    {
      title: "Tokens", key: "tokens", width: 140, align: "right",
      render: (_, r) => {
        const i = Number(r.input_tokens) || 0;
        const o = Number(r.output_tokens) || 0;
        if (!i && !o) return <span style={{ color: "#cbd5e1" }}>—</span>;
        return (
          <Tooltip title={`${num(i)} in · ${num(o)} out`}>
            <span style={{ fontSize: 12, color: "#475569", fontVariantNumeric: "tabular-nums" }}>
              {num(i)} <span style={{ color: "#cbd5e1" }}>/</span> {num(o)}
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: "Cost", dataIndex: "estimated_cost_usd", width: 110, align: "right",
      render: (v) => <span style={{ fontSize: 12.5, color: "#475569", fontVariantNumeric: "tabular-nums" }}>{usd(v)}</span>,
    },
    {
      title: "Time", dataIndex: "response_time_ms", width: 95, align: "right",
      render: (v) => <span style={{ fontSize: 12.5, color: "#475569", fontVariantNumeric: "tabular-nums" }}>{duration(v)}</span>,
    },
    {
      title: "Result", key: "result", width: 220,
      /* Status and error in one column: a failure without its reason sends the
         reader to the logs, which is the trip this tab exists to save. */
      render: (_, r) => {
        const ok = ["success", "ok", "completed", "1", "true"].includes(String(r.status).toLowerCase());
        if (ok) return chip("Success", TONE.green);
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {chip(titleCase(r.status || "Failed"), TONE.red)}
            {r.error_message && (
              <Tooltip title={r.error_message}>
                <span style={{ fontSize: 11, color: "#dc2626", maxWidth: 200, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {r.error_message}
                </span>
              </Tooltip>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <>
      <TileGrid>
        <StatTile icon={<ApiOutlined />} label="API calls" value={num(t.calls)} color="#6366f1" bg="#eef2ff" />
        <StatTile icon={<ImportOutlined />} label="Input tokens" value={num(t.input_tokens)} color="#2563eb" bg="#eff6ff" />
        <StatTile icon={<ExportOutlined />} label="Output tokens" value={num(t.output_tokens)} color="#7c3aed" bg="#f5f3ff" />
        <StatTile icon={<DollarOutlined />} label="Estimated cost" value={usd(t.cost_usd)} color="#16a34a" bg="#f0fdf4" />
      </TileGrid>

      {byType.length > 1 && (
        <div style={{ ...cardStyle, marginBottom: 20 }}>
          <PanelHeader
            icon={<ApiOutlined />}
            title="Calls by type"
            subtitle="On this page of results"
            iconBg="linear-gradient(135deg,#6366f1,#4f46e5)"
          />
          <div style={{ padding: "16px 12px 8px", height: 220 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={byType} margin={{ top: 4, right: 12, bottom: 4, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} width={34} />
                <RTooltip
                  cursor={{ fill: "#f8fafc" }}
                  contentStyle={{ borderRadius: 10, border: "1px solid #e2e8f0", fontSize: 12 }}
                />
                <Bar dataKey="calls" fill="#6366f1" radius={[6, 6, 0, 0]} maxBarSize={48} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div style={cardStyle}>
        <PanelHeader
          icon={<ApiOutlined />}
          title="AI API usage"
          subtitle="Every call, with its model, latency and outcome"
          iconBg="linear-gradient(135deg,#6366f1,#4f46e5)"
        />
        <PanelTable
          columns={columns}
          rows={p.rows}
          loading={p.loading}
          page={p.page}
          total={p.total}
          perPage={p.perPage}
          onPage={p.goToPage}
          empty="No AI calls recorded for this account yet."
          scrollX={1180}
        />
      </div>
    </>
  );
};

export default AiUsageTab;
